import { useCallback } from 'react';
import classnames from 'clsx';

import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import style from './checkbox-slider.module.less';

import type React from 'react';

type CheckboxSliderProps = {
  checkboxId: string;
  checked: boolean;
  className?: string;
  disabled?: boolean;
  disabledTooltipMessage?: React.ReactNode;
  labelText?: React.ReactNode;
  onChange: (checked: boolean) => void;
  amplitudeTrackingId?: string;
  shouldStopEventPropagation?: boolean;
};

export const CheckboxSlider: React.FC<CheckboxSliderProps> = ({
  checkboxId,
  checked = false,
  className = '',
  disabled = false,
  disabledTooltipMessage = null,
  labelText = '',
  onChange,
  amplitudeTrackingId,
  shouldStopEventPropagation = false,
}) => {
  const onClick = useCallback(() => {
    if (disabled) {
      return;
    }
    onChange(!checked);
  }, [checked, disabled, onChange]);

  const handlePropagation = useCallback(
    (event: React.MouseEvent) => {
      if (shouldStopEventPropagation) {
        event.stopPropagation();
      }
    },
    [shouldStopEventPropagation],
  );

  const checkbox = (
    <div className={classnames(style['checkbox-slider'], className)} onClick={handlePropagation}>
      <input
        checked={!!checked}
        data-amplitude-id={amplitudeTrackingId}
        disabled={disabled}
        id={checkboxId}
        onChange={onClick}
        type='checkbox'
        value={checked ? 'true' : ''}
      />
      <label htmlFor={checkboxId}>
        <span className={style.slider} />
        <span className={style['checkbox-slider-text']}>{labelText}</span>
      </label>
    </div>
  );

  if (disabled && disabledTooltipMessage) {
    return (
      <Tooltip>
        <span>{checkbox}</span>
        <TooltipContent>{disabledTooltipMessage}</TooltipContent>
      </Tooltip>
    );
  }

  return checkbox;
};
